<template>
	<div v-if="features.length" class="w-full">
		<div
			class="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between"
		>
			<DefaultHeading :size="2" :horizontal-line="false" class="mb-0">
				Features
			</DefaultHeading>
			<div class="mt-2 w-full lg:mt-0 lg:w-3/12">
				<CarbarInputSecondary
					v-model="search"
					placeholder="Search features"
					name="search_features"
					input-class="pr-10 min-w-100"
					:has-leading-icon="true"
					:has-trailing-icon="search ? true : false"
					@click-trailing-icon="search = ''"
				>
					<template #leadingIcon>
						<IconSearch class="text-gray-500" />
					</template>

					<template #trailingIcon>
						<IconTimes class="cursor-pointer text-gray-500" />
					</template>
				</CarbarInputSecondary>
			</div>
		</div>

		<div v-if="search" class="">
			<div class="border-b pb-3">
				<span v-if="filteredFeatures.length" class="text-gray-575">
					{{ filteredFeatures.length }} features found
				</span>
			</div>
			<div class="-mx-2 mt-6 flex flex-wrap">
				<div
					v-for="(category, index) in getCategories(filteredFeatures)"
					:key="index"
					class="w-full px-2 lg:w-4/12"
				>
					<span class="text-lg font-bold text-gray-700">
						{{ category }}
					</span>
					<UnorderedList
						:items="
							getFeaturesByCategory(category, filteredFeatures)
						"
						:show-list-icon="false"
						class="mt-4 space-y-2"
					/>
				</div>
				<span
					v-if="filteredFeatures.length === 0"
					class="text-gray-575"
				>
					No matching features for '{{ search }}'
				</span>
			</div>
		</div>

		<DefaultTab v-else :tabs="tabs" @change-tab="getGTM">
			<template
				v-for="(tab, index) in tabs"
				:key="index"
				#[_kebabCase(tab.label)]="slotProps"
			>
				<ClientOnly>
					<UnorderedList
						:items="
							getFeaturesByCategory(tab.label).slice(
								0,
								getTotalFeaturesToShow
							)
						"
						:show-list-icon="false"
						class="lg:col-count-3 m-0 space-y-2"
					/>
					<SlideDownAccordion
						v-if="
							getFeaturesByCategory(tab.label).length >
							getTotalFeaturesToShow
						"
						:disclosure-button-at-bottom="true"
						class="'w-full'"
					>
						<template #disclosure-panel>
							<UnorderedList
								:items="
									getFeaturesByCategory(tab.label).slice(
										getTotalFeaturesToShow
									)
								"
								:show-list-icon="false"
								class="lg:col-count-3 mb-0 mt-1 space-y-2"
							/>
						</template>
						<template #disclosure-button="{ open }">
							<CarbarButton
								class="mt-8"
								:is-full-width="false"
								:is-outline="true"
							>
								{{ open ? 'Show less' : 'Show more' }}
							</CarbarButton>
						</template>
					</SlideDownAccordion>
				</ClientOnly>
			</template>
		</DefaultTab>

		<div class="mt-8 flex justify-end">
			<div class="w-28 md:w-40">
				<CarbarImage
					image="img/logo-redbook.webp"
					alt-text="powered by Redbook"
				/>
			</div>
		</div>
	</div>
</template>

<script setup>
import IconSearch from '@/assets/svg/common/ico-search.svg'
import IconTimes from '@/assets/svg/common/ico-times.svg'
import CarbarImage from '@/components/CarbarImage.vue'
import SlideDownAccordion from '@/components/accordions/SlideDownAccordion.vue'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import DefaultHeading from '@/components/headings/DefaultHeading.vue'
import CarbarInputSecondary from '@/components/inputs/InputSecondary.vue'
import UnorderedList from '@/components/lists/UnorderedList.vue'
import DefaultTab from '@/components/tabs/DefaultTab.vue'
import { useProductCatalog } from '@/stores/product-catalog'
import { useProductDetails } from '@/stores/product-details'
import { kebabCase as _kebabCase } from 'lodash-es'
import { computed, ref } from 'vue'

const props = defineProps({
	product: {
		type: Object,
		required: false,
	},
	pageType: {
		type: String,
		default: 'subscribe',
		validator: (value) =>
			['subscribe', 'used', 'group-subscription'].includes(value),
	},
})

const productDetails = useProductDetails()
const productCatalog = useProductCatalog()
const runtimeConfig = useRuntimeConfig()
const route = useRoute()

const search = ref('')
const isSubscription = computed(() => props.pageType === 'subscribe')
const isBuyUsed = computed(() => props.pageType === 'used')
const isGroupSubscription = computed(
	() => props.pageType === 'group-subscription'
)

const features = computed(() => {
	if (!props.product.features || props.product.features.length === 0)
		return []

	return props.product.features.filter((feature) => !!feature.category)
})

const filteredFeatures = computed(() => {
	if (!search.value) {
		return []
	}

	return features.value.filter((feature) =>
		feature.label.toLowerCase().includes(search.value.toLowerCase())
	)
})

const getCategories = (data = null) => {
	const categories = (data || features.value).map(
		(feature) => feature.category
	)
	return [...new Set(categories)]
}

const tabs = computed(() =>
	getCategories().map((category) => ({
		label: category,
	}))
)

const getFeaturesByCategory = (category, data = null) =>
	(data || features.value).filter((feature) => feature.category === category)

const getTotalFeaturesToShow = computed(() => {
	if (isMobileView()) {
		return 10
	}

	return 12
})

const getGTM = (index) => {
	if (isSubscription.value) {
		productDetails.gtmTrack({
			event: 'subscription_car_features',
			product_category: 'Subscription',
			product: productDetails.product,
			page: productCatalog.productsPagination.currentPage,
			features: tabs.value[index].label,
			subscription_car_primary_url:
				runtimeConfig.public.APP_URL + route.path,
		})
	} else if (isBuyUsed.value) {
		productDetails.gtmTrack({
			event: 'buy_used_features',
			product_category: 'Buy Used',
			product: productDetails.product,
			page: productCatalog.productsPagination.currentPage,
			features: tabs.value[index].label,
			subscription_duration: productDetails.subscriptionDuration,
			buy_used_car_primary_url: runtimeConfig.public.APP_URL + route.path,
		})
	} else if (isGroupSubscription.value) {
		return
	}
}
</script>
